/*
 * NEW ARRIVAL HERO — motion driver
 * Modelle · 2026
 *
 * Two jobs, both cheap:
 *
 *   1. Intro cleanup. Each headline word animates inside an overflow:hidden
 *      mask. Once the last word has landed the mask is released, so the
 *      headline's text-shadow is not clipped at the word boxes for the rest of
 *      the page's life. Purely additive — if this never runs, the words still
 *      animate, they just keep a tighter shadow.
 *
 *   2. Scroll parallax. Publishes a single normalised progress value (0 → 1)
 *      as the custom property --na-p on the section element. CSS does the rest.
 *
 * Performance notes:
 *   · The frame loop writes ONE custom property on ONE element per frame.
 *     Everything downstream is transform-only, so no layout and no paint.
 *   · Scroll position is read from window.pageYOffset, never from
 *     getBoundingClientRect, so a scroll never forces a synchronous layout.
 *     The section's offset/height are measured once and re-measured only on
 *     resize.
 *   · The loop is not a permanent rAF. It starts on scroll, eases toward the
 *     target and stops itself once it arrives, so an idle hero costs nothing.
 *   · An IntersectionObserver halts it entirely when the hero is off-screen.
 *   · will-change is applied only while the loop is running (via .is-moving)
 *     and dropped the moment it stops, so we never hold idle layers on the GPU.
 *   · Honours prefers-reduced-motion: no parallax at all.
 */

(function () {
  'use strict';

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
  }

  function scrollY() {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  /* ---------------------------------------------------------------
     1. Release the headline word masks once the intro has finished
  --------------------------------------------------------------- */
  function setupIntro(hero) {
    var words = hero.querySelectorAll('[data-na-word-inner]');
    var settled = false;

    function finish() {
      if (settled) return;
      settled = true;
      hero.classList.add('is-intro-done');
    }

    if (!words.length || hero.getAttribute('data-na-animate') !== 'true') {
      finish();
      return;
    }

    words[words.length - 1].addEventListener('animationend', finish, { once: true });

    // Safety net — a background tab can swallow animationend entirely.
    setTimeout(finish, 3000);
  }

  /* ---------------------------------------------------------------
     2. Keep the headline inside the viewport

     The headline is a single nowrap line, so nothing in CSS can make it
     shrink to fit: a longer word, an uppercase headline or a narrow phone
     all push it past the edge. We measure the real rendered width at a
     known reference size and publish a hard px cap that CSS min()s against
     the configured vw size.

     Measured at a fixed 100px so the result is independent of the clamp,
     the viewport and the font — em-based letter-spacing scales with it, so
     the ratio holds at any size. Runs on load, after webfonts land, and on
     resize; never per frame.
  --------------------------------------------------------------- */
  function fitHeadline(hero) {
    var headline = hero.querySelector('[data-na-headline]');
    if (!headline || !headline.firstChild) return;

    // Lift any previous cap so the measurement sees the natural width.
    hero.style.setProperty('--na-headline-fit', '9999px');

    var styles = window.getComputedStyle(headline);
    var available =
      headline.clientWidth -
      (parseFloat(styles.paddingLeft) || 0) -
      (parseFloat(styles.paddingRight) || 0);
    if (available <= 0) return;

    var previous = headline.style.fontSize;
    headline.style.fontSize = '100px';

    var natural = 0;
    if (typeof document.createRange === 'function') {
      var range = document.createRange();
      range.selectNodeContents(headline);
      natural = range.getBoundingClientRect().width;
    }
    if (!natural) natural = headline.scrollWidth;

    headline.style.fontSize = previous;
    if (!natural) return;

    // 0.995 keeps a hair of slack so sub-pixel rounding cannot clip a glyph.
    var cap = (100 * available) / natural * 0.995;
    hero.style.setProperty('--na-headline-fit', cap.toFixed(2) + 'px');
  }

  /* ---------------------------------------------------------------
     3. Scroll-linked parallax
  --------------------------------------------------------------- */
  function setupParallax(hero) {
    if (reduceQuery.matches) return;
    if (hero.getAttribute('data-na-scroll') !== 'true') return;

    var offsetTop = 0;
    var height = 1;
    var target = 0;
    var current = 0;
    var visible = true;
    var running = false;
    var frameId = 0;
    var measureId = 0;

    function measure() {
      measureId = 0;
      var rect = hero.getBoundingClientRect();
      offsetTop = rect.top + scrollY();
      height = rect.height || 1;
      readTarget();
      start();
    }

    function readTarget() {
      target = clamp((scrollY() - offsetTop) / height, 0, 1);
    }

    function frame() {
      // Ease toward the target so momentum scrolling on iOS stays fluid
      // instead of stepping with the (coarser) scroll event cadence.
      current += (target - current) * 0.14;
      if (Math.abs(target - current) < 0.0004) current = target;

      hero.style.setProperty('--na-p', current.toFixed(4));

      if (visible && current !== target) {
        frameId = window.requestAnimationFrame(frame);
        return;
      }

      // Arrived (or scrolled out of view) — stand down.
      frameId = 0;
      running = false;
      hero.classList.remove('is-moving');
    }

    function start() {
      if (running || !visible) return;
      if (current === target) return;
      running = true;
      hero.classList.add('is-moving');
      frameId = window.requestAnimationFrame(frame);
    }

    function onScroll() {
      readTarget();
      start();
    }

    function onResize() {
      if (measureId) return;
      measureId = window.requestAnimationFrame(measure);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });

    // Fonts landing changes the hero's height on some templates.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(onResize).catch(function () {});
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) {
            readTarget();
            start();
          } else if (frameId) {
            window.cancelAnimationFrame(frameId);
            frameId = 0;
            running = false;
            hero.classList.remove('is-moving');
            // Land on the exact value so returning to the hero is never a jump.
            current = target;
            hero.style.setProperty('--na-p', current.toFixed(4));
          }
        },
        { rootMargin: '120px 0px' }
      );
      observer.observe(hero);
    }

    // Stop honouring motion the moment the OS preference flips.
    var onPreferenceChange = function () {
      if (!reduceQuery.matches) return;
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = 0;
      running = false;
      hero.classList.remove('is-moving');
      hero.style.setProperty('--na-p', '0');
    };
    if (typeof reduceQuery.addEventListener === 'function') {
      reduceQuery.addEventListener('change', onPreferenceChange);
    }

    measure();
  }

  /* Re-fit on load, after webfonts swap in, and on any resize. Deliberately
     outside setupParallax, which bails early under reduced-motion — the
     headline still has to fit. */
  function setupFit(hero) {
    var pending = 0;

    function run() {
      pending = 0;
      fitHeadline(hero);
    }

    function schedule() {
      if (pending) return;
      pending = window.requestAnimationFrame(run);
    }

    run();
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(schedule).catch(function () {});
    }
    window.addEventListener('load', schedule);
  }

  function init(root) {
    var heroes = (root || document).querySelectorAll('[data-na-hero]');
    for (var i = 0; i < heroes.length; i++) {
      var hero = heroes[i];
      if (hero.dataset.naMotionReady === 'true') continue;
      hero.dataset.naMotionReady = 'true';
      setupIntro(hero);
      setupFit(hero);
      setupParallax(hero);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor re-renders the section on every settings change.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
